import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import Login       from "./views/Login";
import PatientList from "./views/PatientList";
import AddPatient  from "./views/AddPatient";
import EditPatient       from "./views/EditPatient";
import EditPrescriptions from "./views/EditPrescriptions";
import Layout      from "./components/Layout";

import { primaryColor } from "./config/constants";

const theme = createTheme({
  palette: {
    primary: { main: primaryColor },
    background: { default: "#F1F3F4" },
  },
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
});

const isAuthenticated = () => !!localStorage.getItem("token");

function PrivateRoute({ children }) {
  return isAuthenticated() ? children : <Navigate to="/login" replace />;
}

// Handles Unity deep-link URLs that carry the JWT in the path.
// Stores the token in localStorage then redirects to the target internal route.
function TokenAutoLogin({ getRedirect }) {
  const params   = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (params.token) {
      localStorage.setItem("token", params.token);
    }
    navigate(getRedirect(params), { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter basename="/">
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* Unity deep-link entry points — token is embedded in the URL path */}
          <Route
            path="/edit-patient/:token/:id"
            element={<TokenAutoLogin getRedirect={({ id }) => `/patients/${id}/edit`} />}
          />
          <Route
            path="/add-patient/:token"
            element={<TokenAutoLogin getRedirect={() => `/patients/new`} />}
          />
          <Route
            path="/prescriptions/:mode/:token/:id"
            element={<TokenAutoLogin getRedirect={({ id }) => `/patients/${id}/edit`} />}
          />

          <Route
            path="/"
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
            <Route index element={<Navigate to="/patients" replace />} />
            <Route path="patients"          element={<PatientList />} />
            <Route path="patients/new"                   element={<AddPatient />} />
            <Route path="patients/:id/edit"             element={<EditPatient />} />
            <Route path="patients/:id/prescriptions"    element={<EditPrescriptions />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <ToastContainer position="top-right" autoClose={3000} />
    </ThemeProvider>
  );
}
